/**
 * Pure positioning helper for the tour narration callout (Epic 4, story 4.4).
 *
 * design-contract.md → §Surface 3.2: "The callout positions relative to the anchor rect
 * (above/below/beside per available space) and must stay within viewport bounds."
 *
 * No DOM, no Angular — a deterministic function so the viewport-bounded placement is unit-testable.
 */

/** A bounding rectangle in viewport coordinates. */
export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

/** Viewport dimensions. */
export interface Viewport {
  width: number
  height: number
}

/** Callout size to place. */
export interface CalloutSize {
  width: number
  height: number
}

export type CalloutPlacement = 'top' | 'bottom' | 'left' | 'right'

export interface CalloutPosition {
  top: number
  left: number
  placement: CalloutPlacement
}

/** Gap between the anchor and the callout, in px. */
const GAP = 12
/** Minimum margin the callout keeps from the viewport edge, in px. */
const MARGIN = 8

/** Clamp `value` into `[min, max]`. When the range is degenerate (min > max) return `min`. */
function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

/**
 * Compute a viewport-bounded position for the callout relative to `rect`.
 *
 * Preference order: below the anchor → flip above if it would overflow the bottom → beside (right,
 * then left) if neither vertical placement fits. Whatever placement is chosen, the final `top`/`left`
 * are clamped so the whole callout stays within the viewport (minus a small margin).
 */
export function computeCalloutPosition(rect: Rect, viewport: Viewport, callout: CalloutSize): CalloutPosition {
  const spaceBelow = viewport.height - (rect.top + rect.height)
  const spaceAbove = rect.top

  let placement: CalloutPlacement
  let top: number
  let left: number

  if (spaceBelow >= callout.height + GAP) {
    // Prefer below the anchor.
    placement = 'bottom'
    top = rect.top + rect.height + GAP
    left = rect.left + rect.width / 2 - callout.width / 2
  } else if (spaceAbove >= callout.height + GAP) {
    // Flip above when there is no room below.
    placement = 'top'
    top = rect.top - callout.height - GAP
    left = rect.left + rect.width / 2 - callout.width / 2
  } else if (viewport.width - (rect.left + rect.width) >= callout.width + GAP) {
    // Beside — to the right.
    placement = 'right'
    left = rect.left + rect.width + GAP
    top = rect.top + rect.height / 2 - callout.height / 2
  } else {
    // Beside — to the left (last resort).
    placement = 'left'
    left = rect.left - callout.width - GAP
    top = rect.top + rect.height / 2 - callout.height / 2
  }

  // Clamp fully within the viewport so the callout never leaves the screen.
  top = clamp(top, MARGIN, viewport.height - callout.height - MARGIN)
  left = clamp(left, MARGIN, viewport.width - callout.width - MARGIN)

  return { top, left, placement }
}

/**
 * Which side of the anchor the tour's floating arrow sits on (CNCT-6771). The arrow points back at the
 * anchor, so `'right'` = the arrow sits to the anchor's right and points left.
 */
export type ArrowSide = 'top' | 'bottom' | 'left' | 'right'

/** A placed arrow: the chosen side plus the glyph box's viewport-coordinate top-left. */
export interface ArrowPosition {
  side: ArrowSide
  top: number
  left: number
}

/** The arrow glyph's box, in px. */
export const ARROW_SIZE = 32

const OPPOSITE_SIDE: Record<ArrowSide, ArrowSide> = { right: 'left', left: 'right', top: 'bottom', bottom: 'top' }

/** Whether `side` has room for an arrow of `size` (plus the standard gap) between anchor and viewport edge. */
function sideFits(side: ArrowSide, rect: Rect, viewport: Viewport, size: number): boolean {
  switch (side) {
    case 'right':
      return viewport.width - (rect.left + rect.width) >= size + GAP + MARGIN
    case 'left':
      return rect.left >= size + GAP + MARGIN
    case 'bottom':
      return viewport.height - (rect.top + rect.height) >= size + GAP + MARGIN
    case 'top':
      return rect.top >= size + GAP + MARGIN
  }
}

/**
 * Place the floating arrow beside `rect`, honoring the step's preferred `side` (design contract
 * §Surface 3.2: left-nav rows are pointed at from the right, account-menu rows from the left, the
 * header launcher from below).
 *
 * The arrow is offset from the anchor edge by the same `GAP` the callout uses, so it never overlaps or
 * obscures the element it points at. When the preferred side has no room — a narrow viewport, or an
 * anchor hugging that edge — it flips to the opposite side, then to any side that fits, before falling
 * back to the preference. The final coordinates are clamped inside the viewport so a resize can never
 * strand the arrow off-screen.
 */
export function computeArrowPosition(rect: Rect, viewport: Viewport, side: ArrowSide = 'right', size: number = ARROW_SIZE): ArrowPosition {
  const candidates: ArrowSide[] = [side, OPPOSITE_SIDE[side], 'right', 'left', 'bottom', 'top']
  const chosen = candidates.find(candidate => sideFits(candidate, rect, viewport, size)) ?? side

  let top: number
  let left: number
  switch (chosen) {
    case 'right':
      left = rect.left + rect.width + GAP
      top = rect.top + rect.height / 2 - size / 2
      break
    case 'left':
      left = rect.left - GAP - size
      top = rect.top + rect.height / 2 - size / 2
      break
    case 'bottom':
      top = rect.top + rect.height + GAP
      left = rect.left + rect.width / 2 - size / 2
      break
    case 'top':
      top = rect.top - GAP - size
      left = rect.left + rect.width / 2 - size / 2
      break
  }

  return {
    side: chosen,
    top: clamp(top, MARGIN, viewport.height - size - MARGIN),
    left: clamp(left, MARGIN, viewport.width - size - MARGIN),
  }
}

/** Width of the drawer-peek bubble, in px. */
export const PEEK_WIDTH = 216
/** Side of the peek bubble's caret square (rotated 45°), in px. */
export const PEEK_CARET = 12

/** A placed peek bubble: its viewport-coordinate top-left, plus the caret's offset within it. */
export interface PeekPosition {
  top: number
  left: number
  /** Caret offset from the bubble's own left edge, so it points at the anchor's horizontal centre. */
  caretLeft: number
}

/**
 * Place the drawer-peek bubble below `rect` (CNCT-6771) — the hamburger is at the top of the header, so
 * below is the only side with room, and the bubble hangs left-aligned to it.
 *
 * The bubble is clamped inside the viewport, and the caret is then clamped inside the bubble, so a
 * hamburger near either edge still gets a caret that points at it rather than one hanging off the
 * corner.
 */
export function computePeekPosition(rect: Rect, viewport: Viewport, width: number = PEEK_WIDTH): PeekPosition {
  const top = rect.top + rect.height + GAP
  const maxLeft = viewport.width - width - MARGIN
  const left = clamp(rect.left, MARGIN, maxLeft)
  const anchorCentre = rect.left + rect.width / 2
  const caretLeft = clamp(anchorCentre - left - PEEK_CARET / 2, PEEK_CARET, width - PEEK_CARET * 2)
  return { top, left, caretLeft }
}
